import { useCallback } from 'react';

import { ButtonType } from '@xemplo/button';
import { DawnTriangleAlert } from '@xemplo/icons';
import { Prompt, PromptDialogSlot, usePromptDialog } from '@xemplo/prompt-dialog';
import { Colour } from '@xemplo/style-constants';

type UseClosePromptProps = {
  closeDialog: () => void;
};

export const useClosePrompt = ({ closeDialog }: UseClosePromptProps) => {
  const prompt = usePromptDialog({ id: 'create-amendment-close-prompt' });

  const handleConfirm = useCallback(() => {
    prompt.closeDialog();
    closeDialog();
  }, [closeDialog, prompt]);

  const confirmationPrompt = (
    <Prompt.Dialog {...prompt.dialogProps}>
      <Prompt {...prompt.promptProps}>
        <Prompt.Slot
          name={PromptDialogSlot.CloseButton}
          render={
            <Prompt.CloseButton
              id="close-prompt-button"
              ariaLabel="close prompt"
              onClick={prompt.closeDialog}
            />
          }
        />
        <Prompt.Slot
          name={PromptDialogSlot.Header}
          render={
            <Prompt.Header
              icon={<DawnTriangleAlert color={Colour.Red[500]} />}
              title="Are you sure you want to cancel?"
            />
          }
        />
        <Prompt.Slot
          name={PromptDialogSlot.Body}
          render={<Prompt.Body>You will lose any unsubmitted data.</Prompt.Body>}
        />
        <Prompt.Slot
          name={PromptDialogSlot.Footer}
          render={
            <Prompt.Footer
              btnPrimary={{
                label: 'Yes',
                ariaLabel: 'Yes Btn',
                onClick: handleConfirm,
                type: ButtonType.Primary,
              }}
              btnSecondary={{
                label: 'No',
                ariaLabel: 'No Btn',
                onClick: prompt.closeDialog,
                type: ButtonType.Secondary,
              }}
            />
          }
        />
      </Prompt>
    </Prompt.Dialog>
  );

  return {
    requestClose: prompt.openModalDialog,
    confirmationPrompt,
  };
};
